import React, { useMemo } from 'react';

import { L3_PATHS } from 'Deer/constants/routes';
import { ColDef } from 'ag-grid-community';
import { useRouteMatch } from 'react-router-dom';
import { CFProgressBar } from 'screens/ContinuousFeedback/components/CFProgressBar';
import { EmployeeRenderer } from 'screens/ContinuousFeedback/components/EmployeeRenderer';
import { useGetMyCompetenciesQuery } from 'screens/ContinuousFeedback/dataHooks';

import { AgGridHeader } from 'uiToolkitV1/AgGridHeader';

import { Row } from '../types/myCompetencies';
import { AgGridConfig } from '../types/useAgGridConfig';

export const useAgGridConfig = (): AgGridConfig => {
  const { data: myCompetenciesData } = useGetMyCompetenciesQuery() ?? {};

  const { columns, rowData } = myCompetenciesData ?? {};

  const isTreeData = !!useRouteMatch(L3_PATHS.team.directReports.develop.manage);

  const defaultColDef = useMemo<ColDef<Row>>(
    () => ({
      autoHeight: true,
      wrapText: true,
      headerComponentParams: {
        enableMenu: true,
        enableSorting: true,
      },
      menuTabs: ['filterMenuTab'],
      headerComponent: AgGridHeader,
      filter: 'agTextColumnFilter',
      cellStyle: { display: 'flex', alignItems: 'center', overflow: 'hidden' },
    }),
    []
  );

  const autoGroupColumnDef = useMemo<ColDef<Row>>(
    () => ({
      headerName: 'Employee Name',
      headerComponentParams: {
        enableMenu: true,
        enableSorting: false,
      },
      minWidth: 320,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: EmployeeRenderer,
      },
      filter: 'agMultiColumnFilter',
      filterParams: {
        filters: [
          {
            filter: 'agSetColumnFilter',
            display: 'accordion',
            title: 'Filter by Employee',
            filterParams: {
              applyMiniFilterWhileTyping: true,
              getValue: ({ data }) => data.employee.value.displayName,
            },
          },
          {
            filter: 'agSetColumnFilter',
            display: 'accordion',
            title: 'Filter by Competency',
            filterParams: {
              applyMiniFilterWhileTyping: true,
              values: rowData?.filter((row) => !!row.competency.value).map((row) => row.competency.value),
            },
          },
        ],
      },
      cellStyle: { display: 'flex', alignItems: 'center', overflow: 'hidden' },
    }),
    [rowData]
  );

  const columnDefs = useMemo<ColDef<Row>[]>(() => {
    if (!columns) return null;
    const colDefs: ColDef<Row>[] = [];

    if (columns.competency && !isTreeData) {
      colDefs.push({
        field: columns.competency.id,
        headerName: columns.competency.displayName,
        valueGetter: ({ data }) => data.competency.value,
        cellRenderer: ({ value }) => (
          <span style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{value}</span>
        ),
        minWidth: 320,
        filter: 'agSetColumnFilter',
        sortable: true,
        flex: 1,
      });
    }

    if (columns.progress) {
      colDefs.push({
        field: columns.progress.id,
        headerName: columns.progress.displayName,
        valueGetter: ({ data }) => data.progress.value,
        cellRenderer: CFProgressBar,
        minWidth: 320,
        filter: 'agMultiColumnFilter',
        headerComponentParams: {
          enableMenu: true,
          enableSorting: false,
        },
        filterParams: {
          filters: [
            {
              filter: 'agSetColumnFilter',
              display: 'accordion',
              title: 'Filter by Label',
              filterParams: {
                applyMiniFilterWhileTyping: true,
                getValue: ({ data }) => data.progress.value.label,
              },
            },
            {
              filter: 'agNumberColumnFilter',
              display: 'accordion',
              title: 'Filter by Score',
              filterParams: {
                getValue: ({ data }) => data.progress.value.displayScore,
              },
            },
          ],
        },
      });
    }

    if (columns.praise) {
      colDefs.push({
        field: columns.praise.id,
        headerName: columns.praise.displayName,
        valueGetter: ({ data }) => data.praise.value,
        width: 120,
        filter: 'agNumberColumnFilter',
        sortable: true,
      });
    }

    if (columns.advice) {
      colDefs.push({
        field: columns.advice.id,
        headerName: columns.advice.displayName,
        valueGetter: ({ data }) => data.advice.value,
        width: 120,
        filter: 'agNumberColumnFilter',
        sortable: true,
      });
    }

    return colDefs;
  }, [columns, isTreeData]);

  return {
    defaultColDef,
    autoGroupColumnDef: isTreeData ? autoGroupColumnDef : null,
    columnDefs,
    rowData,
    isTreeData,
  };
};
